import React, { useEffect, useRef, useState } from 'react';
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { useGoogleLogin } from '@react-oauth/google';
import toast, { Toaster } from 'react-hot-toast';
import { Logins, checkImportMemberStatus, getTopFiveSignal, getTopFiveClip } from '../composables/fetchAPI';
import { setToken } from '../composables/tokenManager';
import { useAdBlockDetector } from '../hooks/useAdBlockDetector'; // นำเข้า Hook
import { useNavigate } from 'react-router-dom';
// ✅ 1. ย้าย Style ออกมาไว้นอก Component เป็นค่าคงที่ เพื่อไม่ให้ถูก Re-render ใหม่ทุก 2 วินาที (แก้ปัญหา Tab Crash)
const GLOBAL_STYLES = `
  @keyframes jiggle {
    0%, 100% { transform: rotate(-8deg) scale(1.05); }
    20% { transform: rotate(8deg) scale(0.98); }
    40% { transform: rotate(-6deg) scale(1.03); }
    60% { transform: rotate(6deg) scale(0.97); }
    80% { transform: rotate(-4deg) scale(1.01); }
  }
  .jiggle { animation: jiggle 1.0s infinite; }
  
  @keyframes slideDown {
    0% { transform: translateY(-20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  .animate-slide-down { animation: slideDown 0.3s ease-out forwards; }
  
  @keyframes slideUp {
    0% { transform: translateY(20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  .animate-slide-up { animation: slideUp 0.3s ease-out forwards; }

  /* Animation เลื่อนลงแนวตั้ง */
  @keyframes marquee-vertical-down {
    0% { transform: translateY(-50%); }
    100% { transform: translateY(0%); }
  }
  .animate-marquee-vertical-down {
    animation: marquee-vertical-down 50s linear infinite;
  }

  /* ===== Loading ตอน Login: เหรียญหยอดลงกระปุกหมู ===== */
  @keyframes piggy-gulp {
    0%, 52%, 100% { transform: translateY(0) scale(1, 1); }
    58% { transform: translateY(4px) scale(1.05, 0.93); }
    68% { transform: translateY(-6px) scale(0.97, 1.04); }
    78% { transform: translateY(0) scale(1.01, 0.99); }
  }
  @keyframes coin-drop {
    0% { transform: translate(-50%, -110px) rotateY(0deg); opacity: 0; }
    12% { opacity: 1; }
    52% { transform: translate(-50%, 0) rotateY(720deg); opacity: 1; }
    58%, 100% { transform: translate(-50%, 10px) rotateY(720deg) scale(0.4); opacity: 0; }
  }
  @keyframes piggy-shadow {
    0%, 52%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.35; }
    58% { transform: translateX(-50%) scaleX(1.12); opacity: 0.5; }
    68% { transform: translateX(-50%) scaleX(0.85); opacity: 0.25; }
  }
  @keyframes overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .piggy-gulp { animation: piggy-gulp 1.4s cubic-bezier(.4,0,.2,1) infinite; transform-origin: 50% 100%; }
  .coin-drop { animation: coin-drop 1.4s cubic-bezier(.55,0,.75,.4) infinite; }
  .piggy-shadow { animation: piggy-shadow 1.4s cubic-bezier(.4,0,.2,1) infinite; }
  .overlay-in { animation: overlay-in 0.2s ease-out both; }
  @media (prefers-reduced-motion: reduce) {
    .piggy-gulp, .piggy-shadow { animation: none; }
    .coin-drop { animation: none; opacity: 0; }
  }
`;

type LoginPhase = 'idle' | 'google' | 'verifying';

// หน้าจอ Loading ระหว่าง Login — บังทั้งหน้า กันกดปุ่ม Login ซ้ำ
const LoginLoadingOverlay: React.FC<{ phase: LoginPhase; onCancel: () => void }> = ({ phase, onCancel }) => (
  <div
    role="status"
    aria-live="polite"
    className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/85 backdrop-blur-md px-6 text-center overlay-in"
  >
    <div className="relative w-44 h-44 mb-8" aria-hidden="true">
      <span className="coin-drop absolute left-[48%] top-[14%] w-9 h-9 rounded-full bg-yellow-400 border-[3px] border-yellow-600 text-yellow-700 font-black text-lg leading-[30px] shadow-[0_0_12px_rgba(250,204,21,0.6)]">
        $
      </span>
      <img src="/piggy-usa.png" alt="" className="piggy-gulp relative w-full h-full" />
      <span className="piggy-shadow absolute left-1/2 -bottom-1 w-28 h-3 rounded-[50%] bg-yellow-400/40 blur-sm" />
    </div>

    {phase === 'google' ? (
      <>
        <p className="text-xl font-bold text-yellow-400 mb-2">รอยืนยันบัญชีในหน้าต่าง Google</p>
        <p className="text-sm text-gray-400 max-w-xs leading-relaxed">
          หากไม่เห็นหน้าต่าง Google ให้ตรวจว่าเบราว์เซอร์บล็อกป๊อปอัปอยู่หรือไม่
        </p>
        <button
          onClick={onCancel}
          className="mt-6 px-5 py-2 rounded-full text-sm text-gray-300 border border-white/20 hover:border-white/50 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400"
        >
          ยกเลิกการเข้าสู่ระบบ
        </button>
      </>
    ) : (
      <>
        <p className="text-xl font-bold text-yellow-400 mb-2">กำลังตรวจสอบสิทธิ์สมาชิก</p>
        <p className="text-sm text-gray-400 max-w-xs leading-relaxed">
          ใช้เวลาไม่กี่วินาที โปรดอย่าปิดหรือรีเฟรชหน้านี้
        </p>
      </>
    )}
  </div>
);

const LoginPage: React.FC<{ onLogin: (token: string) => void }> = ({ onLogin }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('translate-x-0');
  const [lastImportStatus, setLastImportStatus] = useState<{ date: string; time: string } | null>(null);
  const [showGuidePopup, setShowGuidePopup] = useState(false);
  const isAdBlockActive = useAdBlockDetector(); // เรียกใช้ตรงนี้
  const navigate = useNavigate(); // 2. เรียกใช้งานตรงนี้
  // useEffect(() => {
  //     // ถ้าตรวจพบว่ามี AdBlock ให้แสดง Toast เตือนผู้ใช้
  //     if (isAdBlockActive) {
  //       toast.error('⚠️ ตรวจพบ AdBlock: หากล็อกอินไม่ผ่านหรือจอลอยค้าง โปรดปิด AdBlock หรือเปลี่ยนเบราว์เซอร์', {
  //         duration: 8000,
  //         position: 'top-center',
  //       });
  //     }
  //   }, [isAdBlockActive]);


  // State สำหรับเก็บข้อมูลคลิปและหุ้น
  const [clips, setClips] = useState<any[]>([]);
  const [signals, setSignals] = useState<any[]>([]);
  const images = [
    '/thaius1.png',
    '/thaius2.png',
    '/thaius3.png',
    '/thaius4.png',
  ];
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle');
  // ใช้ ref ล็อกทันที เพราะ setState ไม่อัปเดตทันใจถ้ากดรัวๆ
  const loginLockRef = useRef(false);

  const startLogin = () => {
    if (loginLockRef.current) return;
    loginLockRef.current = true;
    setLoginPhase('google');
    login();
  };

  const endLogin = () => {
    loginLockRef.current = false;
    setLoginPhase('idle');
  };



  const handleAcceptTerms = () => {
    localStorage.setItem('hasAcceptedTerms', 'true');
    setAcceptedTerms(true);
    setShowTermsPopup(false);
    // สามารถเรียก login() ต่อได้เลยหากต้องการให้กดปุ่มเดียวจบ
    startLogin();
  };

  // 2. สร้างฟังก์ชันตรวจสอบก่อนกด Login
  const handleGoogleLoginClick = () => {
    if (isAdBlockActive) {
      // แจ้งเตือนย้ำอีกรอบก่อนกด
      toast('อย่าลืมปิด AdBlock ชั่วคราวนะครับ หากล็อกอินไม่ผ่าน', { icon: '💡' });
    }

    const hasAccepted = localStorage.getItem('hasAcceptedTerms');
    if (hasAccepted) {
      startLogin();
    } else {
      setShowTermsPopup(true);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout; // ✅ 2. ประกาศตัวแปรเก็บ ID ของ timeout

    const interval = setInterval(() => {
      setSlideDirection('translate-x-full'); // เริ่มเลื่อนไปทางขวา

      timeoutId = setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length); // เปลี่ยนภาพ
        setSlideDirection('translate-x-0'); // รีเซ็ตตำแหน่ง
      }, 500); // ระยะเวลาการเลื่อน (0.5 วินาที)

    }, 2000); // เลื่อนทุกๆ 2 วินาที

    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId); // ✅ เคลียร์ Timeout ทิ้งด้วย ป้องกัน Memory Leak ค้างตอนสลับหน้า
    };
  }, [images.length]);

  const login = useGoogleLogin({
    onSuccess: async tokenResponse => {
      setLoginPhase('verifying');
      try {
        const info = await Logins(tokenResponse.access_token);
        const sessionId = info.sessionId; // ดึง sessionId จาก response
        setToken(info.access_token);
        localStorage.setItem('sessionId', sessionId); // เก็บ sessionId ใน localStorage

        onLogin(info);
        toast.success('Logged in successfully!');
      } catch (e: any) {
        console.error('Login error:', e);
        // ใช้ e.message ซึ่งตอนนี้บรรจุ th_message ไว้แล้ว
        // ถ้าไม่มี message ให้ใช้ fallback เป็นภาษาไทยที่คุณตั้งไว้
        toast.error(e.message || 'การเข้าสู่ระบบล้มเหลวโปรดลองอีกครั้ง');
      } finally {
        endLogin();
      }
    },
    onError: () => {
      endLogin();
      toast.error('Login Failed');
    },
    // ผู้ใช้ปิดหน้าต่าง Google เอง หรือป๊อปอัปถูกบล็อก
    onNonOAuthError: (err) => {
      endLogin();
      if (err.type === 'popup_failed_to_open') {
        toast.error('เปิดหน้าต่าง Google ไม่ได้ โปรดอนุญาตป๊อปอัปแล้วลองอีกครั้ง');
      }
    },
    scope: [
      "https://www.googleapis.com/auth/youtube.readonly",
    ].join(" "),
    flow: "implicit",
  });

  useEffect(() => {
    const fetchImportStatus = async () => {
      try {
        const response = await checkImportMemberStatus();
        setLastImportStatus(response.lastImport);
      } catch (error) {
        console.error('Error fetching import status:', error);
        toast.error('Failed to fetch import status');
      }
    };

    fetchImportStatus();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // ดึง คลิป
        const clipsRes = await getTopFiveClip();
        if (clipsRes) {
          let displayClips: any[] = [];

          // 1. ถ้ามี liveNow ให้แทรกเป็นไอเท็มแรกและติด Tag ไว้
          if (clipsRes.liveNow) {
            displayClips.push({ ...clipsRes.liveNow, isLive: true });
          }

          if (clipsRes.latestVideos) {
            // 2. กรองเอาคลิปที่ thumbnail มีคำว่า '_live' ออก
            const filteredVideos = clipsRes.latestVideos.filter(
              (v: any) => !v.thumbnail?.includes('_live')
            );
            displayClips = [...displayClips, ...filteredVideos];
          }

          // เอาแค่ 5 คลิปแรก
          setClips(displayClips.slice(0, 5));
        }

        // ดึง หุ้น (เอาแค่ 5 ตัวแรก)
        const signalsRes = await getTopFiveSignal();
        if (signalsRes && signalsRes.data) {
          setSignals(signalsRes.data.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching dashboard preview data:', error);
      }
    };

    fetchData();
  }, []);

  const handleMouseEnterGuide = () => {
    setShowGuidePopup(true);
  };

  const handleMouseLeaveGuide = () => {
    setShowGuidePopup(false);
  };

  const handleMouseEnter = () => {
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    setShowPopup(false);
  };

  const handleButtonClick = () => {
    window.open('https://www.youtube.com/channel/UCcFtS_df5r0zxNZ_7JZgmZQ/join', '_blank');
  };

  return (
    <div translate="no" className="relative flex min-h-screen bg-black text-gray-100 font-sans overflow-hidden">
      {/* <Toaster /> */}

      {/* ✅ ดึง Style จากด้านนอกมาแสดงผลเฉยๆ จะไม่ถูกคำนวณใหม่ทุกรอบแล้ว */}
      <style>{GLOBAL_STYLES}</style>

      {/* ================= ซ้าย: แถบสัญญาณหุ้น (LIVE SIGNALS) 1/3 ของจอ ================= */}
      <div className="hidden lg:flex flex-col w-[200px] whitespace-nowrap h-[70vh] absolute left-0 top-0 border-r border-white/10 rounded-br-2xl bg-black/40 backdrop-blur-sm z-0 shadow-[15px_15px_20px_rgba(0,0,0,0.5)]">
        <div className="absolute top-0 left-0 w-full h-12 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full h-12 rounded-br-2xl bg-gradient-to-t from-black to-transparent z-10 pointer-events-none"></div>

        <h3 className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 text-yellow-400 font-bold text-xs tracking-widest bg-black px-3 py-1 rounded-full border border-yellow-500/30">
          🔥 อัปเดตหุ้นล่าสุด
        </h3>

        {signals.length > 0 && (
          <div className="flex-1 overflow-hidden relative mt-12">
            <div className="flex flex-col w-full animate-marquee-vertical-down hover:[animation-play-state:paused]">
              {/* ลูป 3 รอบเพื่อให้เลื่อนต่อเนื่อง */}
              {[...signals, ...signals, ...signals].map((signal, index) => {
                // ดึงค่า S1 และ R1 มาเพื่อเช็คสถานะราคา
                const s1 = signal.details?.lines?.find((l: any) => l.tag === 'S1')?.price;
                const r1 = signal.details?.lines?.find((l: any) => l.tag === 'R1')?.price;
                const currentPrice = signal.price;

                // เช็คเงื่อนไขเทียบราคาเพื่อเปลี่ยนสี
                let priceColor = "text-white";
                let statusIcon = "📊";
                if (currentPrice && r1 && currentPrice > r1) {
                  priceColor = "text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.8)]";
                  statusIcon = "🚀";
                } else if (currentPrice && s1 && currentPrice < s1) {
                  priceColor = "text-red-500 drop-shadow-[0_0_5px_rgba(239,68,68,0.8)]";
                  statusIcon = "⚠️";
                } else if (currentPrice) {
                  priceColor = "text-yellow-400";
                }

                return (
                  <div key={`signal-${index}`} className="relative mx-4 my-2 p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors duration-300 overflow-hidden">

                    <div className="flex justify-between items-center mb-2">
                      <span className="font-extrabold text-base text-white">{cryptoPairLabel(signal.ticker)}</span>

                      {/* กลุ่มสถานะมุมขวาบน (NEW + ไฟกระพริบ) */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[9px] font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 px-1.5 py-0.5 rounded shadow">
                          NEW
                        </span>
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      </div>
                    </div>

                    {/* ข้อมูลราคาเปรียบเทียบ */}
                    <div className="flex flex-col text-xs">

                      {/* Price ราคาล่าสุด (ไฮไลต์สี) */}
                      <div className="flex justify-between items-center bg-black/40 border border-white/10 px-2 py-1.5 rounded-lg shadow-inner mb-2.5">
                        <span className="text-gray-300 text-[10px] font-bold shrink-0 text-sm ">Price </span>
                        <span className={`font-black text-sm truncate pl-2 ${priceColor}`}>
                          {currentPrice ? `$${currentPrice.toFixed(2)}` : '-'}
                        </span>
                      </div>

                      {/* รับ-ต้าน แบบ List (ใส่ truncate ถ้าล้นจะโชว์ ...) */}
                      <div className="flex flex-col gap-1.5 text-[10px] px-1 w-full overflow-hidden">

                        <div className="flex items-center gap-1 w-full overflow-hidden">
                          <span className="text-green-500 font-bold whitespace-nowrap shrink-0 text-sm ">รับ:</span>
                          <span className="text-gray-400 font-medium text-sm truncate">
                            {signal.details?.lines?.filter((l: any) => l.type === 'support').length > 0
                              ? signal.details.lines.filter((l: any) => l.type === 'support').map((l: any) => `$${l.price.toFixed(1)}`).join(', ')
                              : '-'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 w-full overflow-hidden">
                          <span className="text-red-500 font-bold whitespace-nowrap shrink-0 text-sm">ต้าน:</span>
                          <span className="text-gray-400 font-medium text-sm truncate">
                            {signal.details?.lines?.filter((l: any) => l.type === 'resistance').length > 0
                              ? signal.details.lines.filter((l: any) => l.type === 'resistance').map((l: any) => `$${l.price.toFixed(1)}`).join(', ')
                              : '-'}
                          </span>
                        </div>

                      </div>

                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ================= กลาง: หน้า Login หลัก ================= */}
      <div className="flex flex-col items-center justify-center flex-1 z-10 lg:mx-[300px]">

        {/* ปุ่มมุมขวาบน (ของเดิม) */}
        <div
          className="absolute top-4 right-4 z-50"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            onClick={handleButtonClick}
            className="relative bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white font-bold px-6 py-3 rounded-full shadow-lg hover:from-yellow-500 hover:via-red-500 hover:to-pink-500 transition-all transform hover:scale-105"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-yellow-500 via-orange-400 to-red-500 opacity-50 rounded-full blur-md"></span>
            <span className="relative z-10">สมัครสมาชิกช่อง YouTube</span>
          </button>

          {showPopup && (
            <div
              className="absolute right-0 mt-2 border-t-4 border-black w-96 bg-white text-black p-4 rounded-xl shadow-lg animate-slide-down"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <h3 className="text-lg font-bold mb-2">ลงทุนหุ้นอเมริกา</h3>
              <p className="text-sm mb-2">
                แนะนำ การลงทุนหุ้นอเมริกา "เพื่อคนไทย!" <br />
                ออมเงินในหุ้นระยะยาว เพื่อสร้าง Passive Income
              </p>
              <div className="space-y-2 mb-4">
                <a href="https://www.youtube.com/channel/UCcFtS_df5r0zxNZ_7JZgmZQ/join" target="_blank" rel="noopener noreferrer" className="flex items-center text-red-600 hover:underline">
                  <img src="/icons/youtube-icon.png" alt="YouTube Icon" className="w-5 h-4 mr-2" />
                  สมัครสมาชิกช่อง YouTube
                </a>
                <a href="https://shorturl.asia/Eih0Z" target="_blank" rel="noopener noreferrer" className="block text-red-600 hover:underline">👉 โพสสำหรับ "สมาชิกช่อง"</a>
                <a href="https://shorturl.asia/Wm1fJ" target="_blank" rel="noopener noreferrer" className="block text-yellow-600 hover:underline">👨‍🏫 *สมัคร* "เว็ปข่าวหุ้น" ที่เราลงทุนกันอยู่</a>
              </div>
              <img src={images[currentImageIndex]} alt="thai_us_invest" className="w-full h-35 object-cover rounded" />
            </div>
          )}
        </div>

        {/* ส่วน Card Login กลางจอ */}
        <img src="/piggy-usa.png" alt="Piggy Logo" className="w-48 h-48 mb-4 jiggle" />
        <h1 className="text-4xl font-extrabold text-center mb-6 tracking-tight text-yellow-400 drop-shadow-lg">Welcome to WeThaiInvest</h1>

        <button
          onClick={handleGoogleLoginClick}
          disabled={loginPhase !== 'idle'}
          aria-busy={loginPhase !== 'idle'}
          className="flex items-center bg-white text-gray-900 font-medium px-5 py-2 rounded-full shadow border border-gray-300 hover:bg-gray-100 transition-all mb-4 disabled:opacity-60 disabled:cursor-wait"
          style={{ minWidth: 220 }}
        >
          <img src="/icons/Google_Icons.png" alt="Google Logo" className="w-6 h-6 mr-2" />
          <span className="text-base">Log in with Google</span>
        </button>

        <p className="mb-4 text-xs text-gray-400">
          By logging in, you agree to our <a href="/privacy.html" className="underline text-yellow-400" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <div className="w-full max-w-base mt-2 text-center flex flex-col items-center">
          <p className="text-sm mb-4 ">
            การสมัครสมาชิกและการเข้าสู่ระบบดำเนินการโดย Google <span className='text-base'>(หากเพิ่งสมัครสมาชิก อาจใช้เวลา 1-2 วันในการอัปเดตข้อมูล)</span>
          </p>

          <div className="text-[16px] text-yellow-200 leading-relaxed opacity-80 px-4 py-3 bg-white/5 rounded-xl border border-white/10 w-full mb-4 text-center">
            <span className="text-yellow-500 font-bold">หมายเหตุ:</span> ระบบจำกัด 1 บัญชีต่อ 1 อุปกรณ์ การสลับเครื่องจะต้องล็อกอินใหม่ และข้อมูลแนวรับ-ต้านจะถูกบันทึกไว้เฉพาะบนอุปกรณ์ที่ทำรายการเท่านั้น ต้อง Login ใหม่ภายใน 3 วัน
          </div>

          {lastImportStatus ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400/10 rounded-full border border-yellow-400/20 mb-4">
              <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
              <p className="text-sm text-gray-300">
                อัพเดทสมาชิกล่าสุด: <span className="text-yellow-400 font-bold">{lastImportStatus.date} {lastImportStatus.time}</span>
              </p>
            </div>
          ) : (
            <p className="text-xs mb-4 text-gray-400">ยังไม่มีการอัพเดต</p>
          )}

          <div className="text-sm text-white mb-2">
            หากรอแล้วยังเข้าสู่ระบบไม่ได้ โปรดติดต่อมาที่อีเมล{' '}
            <a href="mailto:Support@wethaiinvest.com" className="text-yellow-400 underline">Support@wethaiinvest.com</a>{' '}
            และทำตาม{' '}
            <span className="text-yellow-400 underline cursor-pointer" onClick={(e) => setShowGuidePopup(e => !e)}>คู่มือดังนี้</span>
          </div>
{/* ✅ เพิ่มปุ่มไปหน้า FAQ ตรงนี้ */}
          <button
            onClick={() => navigate('/faq?from=login')}
            className="mb-4 px-6 py-2 mt-2 rounded-full border border-yellow-400 text-yellow-400 font-bold hover:bg-yellow-400 hover:text-black transition-all"
          >
            📚 ศูนย์ช่วยเหลือ (FAQ)
          </button>
          <span className="block text-xs mb-1 text-gray-400">WeThaiInvest is a dashboard for members of the "ลงทุนหุ้นอเมริกา"</span>
          <span className="block text-xs text-gray-400">After logging in with Google, you can access exclusive content for members.</span>
        </div>

        {/* Modal คู่มือการใช้งาน */}
        {showGuidePopup && (
          <div className="fixed bottom-4 right-4 bg-white text-black p-4 rounded-lg shadow-lg animate-slide-up max-w-sm max-h-96 overflow-y-auto z-50">
            <h2 className="text-lg font-bold mb-2">วิธีแจ้งปัญหาการเข้าสู่ระบบ</h2>
            <p className="text-sm mb-2">
              เพื่อให้ทีมงานสามารถตรวจสอบสาเหตุและแก้ไขปัญหาให้ท่านได้อย่างถูกต้องและรวดเร็ว
              สามารถส่งข้อมูลมาที่ <a href="mailto:Support@wethaiinvest.com" className="text-yellow-500 underline">Support@wethaiinvest.com</a>
            </p>
            <ul className="list-disc pl-5 text-sm space-y-2">
              <li>
                <strong>ภาพหน้าจอโปรไฟล์ YouTube:</strong>
                <ul className="list-disc pl-5">
                  <li>กรุณาเข้าไปที่เว็บไซต์ <a href="https://youtube.com/handle" target="_blank" rel="noopener noreferrer" className="text-yellow-500 underline">youtube.com/handle</a></li>
                  <li>แคปหน้าจอให้เห็น ชื่อช่อง (Channel Name) และ @Handle ของท่านอย่างชัดเจน เพื่อใช้ยืนยันความถูกต้องของบัญชีที่เชื่อมต่อ</li>
                  <li>หากไม่พบ @Username เป็นไปได้ว่าบัญชีของท่าน ยังไม่ได้สร้างช่อง กรุณากด "สร้างช่อง" <a href="https://support.google.com/youtube/answer/1646861?hl=th" target="_blank" rel="noopener noreferrer" className="text-yellow-500 underline">วิธีการสร้างช่อง</a></li>
                </ul>
              </li>
              <li>
                <strong>ข้อมูลการสมัครสมาชิก:</strong>
                <ul className="list-disc pl-5">
                  <li>ระบุ วันและเวลา ที่ท่านทำรายการสมัครสมาชิก (โดยเฉพาะกรณีที่เป็นการสมัครสมาชิกใหม่)</li>
                </ul>
              </li>
              <li>
                <strong>รายละเอียดปัญหา:</strong>
                <ul className="list-disc pl-5">
                  <li>แจ้งอาการที่พบ หรือแนบ ภาพหน้าจอข้อความแจ้งเตือน (Error Message) ที่ปรากฏขณะเข้าใช้งาน</li>
                </ul>
              </li>
            </ul>
            <p className="text-sm mt-2">
              <strong>หมายเหตุ:</strong> ในกรณีที่ท่านเพิ่งสมัครสมาชิกใหม่ ระบบอาจใช้เวลาประมาณ 1-2 วัน (สามารถดูเวลาที่อัพเดตได้ที่หน้า Login) ในการอัปเดตฐานข้อมูลเพื่อเปิดสิทธิ์การเข้าใช้งานครับ
            </p>
            <strong>ขั้นตอนการส่งเมล:</strong>
            <img src="/WeThaiInvest.png" alt="WeThaiInvest" className="mr-2" />
            <img src="/WeThaiInvest2.png" alt="WeThaiInvest" className="mr-2 my-2" />
            <div className="text-right">
              <button onClick={() => setShowGuidePopup(false)} className="mt-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
                ปิด
              </button>
            </div>
          </div>
        )}

        {/* Modal สิทธิพิเศษ (ของเดิมที่อยู่มุมซ้ายล่าง) */}
        {showPopup && (
          <div className="fixed bottom-4 left-4 bg-white text-black p-4 rounded-lg shadow-lg animate-slide-up z-50">
            <h2 className="text-lg font-bold mb-2">🎁 สิทธิพิเศษสำหรับสมาชิก</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>รับ การใช้งานเต็ม MAX ที่ "เว็ปช่อง" เช่น พอร์ตส่วนตัว, ฟีเจอร์ช่วยคำนวนต่างๆ, ฯลฯ</li>
              <li>รับ อัพเดททุก "การซื้อ-ขายหุ้น" ของ "แอดอั้ม" พร้อมเหตุผล</li>
              <li>รับ อัพเดททุก "การซื้อ-ขาย" ของ "จารย์ เชย์" พร้อมบทวิเคราะห์</li>
              <li>รับ บทวิเคราะห์หุ้น มากมายตลอดสัปดาห์</li>
              <li>รับ Weekly Report สรุปทุกการซื้อ-ขาย ประจำสัปดาห์</li>
              <li>รับ การตอบคำถามหุ้นจาก "แอดอั้ม" *ในหน้าโพสสมาชิกช่อง*</li>
              <li>ดูคลิป Exclusive เฉพาะ "สมาชิกช่อง"</li>
              <li>รับ 2 หุ้น/เดือนจาก กูรู Alpha Picks เน้นหุ้นพื้นฐานดี</li>
            </ul>
          </div>
        )}


      </div>

      {/* ================= ขวา: แถบคลิปวิดีโอ (LATEST CLIPS) ดันลงมาเพื่อหลบปุ่ม ================= */}
      <div className="hidden lg:flex flex-col w-[270px] h-screen absolute right-0 top-0 border-l border-white/10 bg-black/40 backdrop-blur-sm z-0 shadow-[50px_15px_20px_rgba(0.5,0,0,0.5)]">
        {/* เพิ่ม h-32 (ยาวขึ้น) เพื่อบังคลิปตอนเลื่อนขึ้นไปใกล้ปุ่ม Youtube */}
        <div className="absolute top-0 left-0 w-full h-12 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none"></div>

        {clips.length > 0 && (
          <div className="flex-1 overflow-hidden relative mt-[100px]">
            <div className="flex flex-col w-full animate-marquee-vertical-down hover:[animation-play-state:paused]">
              {[...clips, ...clips, ...clips].map((clip, index) => (
                <a
                  key={`clip-${index}`}
                  href={clip.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mx-4 my-3 bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 hover:scale-[1.02] transition-all duration-300 block"
                >
                  <div className="relative h-36">
                    <img src={clip.thumbnail} alt={clip.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 hover:bg-transparent transition-colors duration-300"></div>
                  </div>
                  <div className="p-3 bg-black/80">
                    <p className="text-xs text-gray-200 line-clamp-2 leading-relaxed" title={clip.title}>{clip.title}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      {showTermsPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-white text-gray-800 p-6 rounded-2xl max-w-lg w-full shadow-2xl animate-slide-up flex flex-col max-h-[90vh]">

            <h2 className="text-xl font-bold mb-4 text-black flex-shrink-0">
              ข้อกำหนดและเงื่อนไขการใช้บริการ และนโยบายความเป็นส่วนตัว
            </h2>
            {/* ส่วนเนื้อหาที่ยาวให้เลื่อนได้ (Scrollable area) */}
            <div className="overflow-y-auto flex-1 mb-6 pr-2 custom-scrollbar">
              <p className="text-sm mb-4 leading-relaxed">
                การเข้าใช้หรือใช้บริการเว็บไซต์ WeThaiInvest ให้ถือว่าผู้ใช้ได้อ่าน ทำความเข้าใจ และตกลงผูกพันตามข้อกำหนดและเงื่อนไขการใช้บริการ รวมถึงนโยบายความเป็นส่วนตัวของเว็บไซต์แล้ว
              </p>
              <p className="text-sm mb-4 leading-relaxed">
                เว็บไซต์นี้จัดให้มีข้อมูล เครื่องมือ และบริการที่เกี่ยวข้องกับการลงทุนเพื่อวัตถุประสงค์ในการให้ข้อมูลและการศึกษาเท่านั้น ไม่ถือเป็นคำแนะนำ คำชี้ชวน หรือการรับประกันผลตอบแทนจากการลงทุน ผู้ใช้ต้องใช้ดุลยพินิจของตนเองและรับผิดชอบต่อการตัดสินใจลงทุนทุกประการ
              </p>
              <p className="text-sm mb-4 leading-relaxed">
                ผู้ใช้รับทราบว่าผู้ให้บริการอาจเก็บ ใช้ และประมวลผลข้อมูลส่วนบุคคลเท่าที่จำเป็น เพื่อการให้บริการ การยืนยันสิทธิการใช้งาน การรักษาความปลอดภัย การปรับปรุงระบบ และการปฏิบัติตามกฎหมายที่เกี่ยวข้อง ทั้งนี้ เป็นไปตามนโยบายความเป็นส่วนตัวของผู้ให้บริการ
              </p>

              <div className="mb-4 text-sm">
                <p>ผู้ใช้สามารถอ่านรายละเอียดเพิ่มเติมได้ที่:</p>
                <a href="/term.pdf" target="_blank" className="text-yellow-600 underline block">ข้อกำหนดและเงื่อนไขการใช้บริการฉบับเต็ม</a>
                <a href="/privacy.html" target="_blank" className="text-yellow-600 underline block">นโยบายความเป็นส่วนตัว</a>
              </div>
            </div>
            {/* ส่วนปุ่มกด (ฟิกซ์ไว้ด้านล่าง) */}
            <div className="flex-shrink-0">
              <label className="flex items-center gap-2 cursor-pointer mb-6">
                <input type="checkbox" className="w-5 h-5" onChange={(e) => setAcceptedTerms(e.target.checked)} />
                <span className="text-sm">ข้าพเจ้าได้อ่าน เข้าใจ และยอมรับข้อกำหนดและเงื่อนไขการใช้บริการ รวมถึงนโยบายความเป็นส่วนตัวของเว็บไซต์ WeThaiInvest แล้ว</span>
              </label>

              <button
                disabled={!acceptedTerms}
                onClick={() => {
                  localStorage.setItem('hasAcceptedTerms', 'true');
                  setShowTermsPopup(false);
                  startLogin();
                }}
                className={`w-full py-3 rounded-xl font-bold ${acceptedTerms ? 'bg-yellow-500 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                ตกลงยอมรับและเริ่มใช้งาน
              </button>
            </div>
          </div>
        </div>
      )}

      {loginPhase !== 'idle' && <LoginLoadingOverlay phase={loginPhase} onCancel={endLogin} />}
    </div>
  );
};

export default LoginPage;